import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface DataPoint {
  hour: string;
  before: number;
  after: number;
}

export const DailyConsumptionChart: React.FC = () => {
  const { t } = useTranslation();
  const [animationProgress, setAnimationProgress] = useState(0);
  const [showAfter, setShowAfter] = useState(false);

  const data: DataPoint[] = [
    { hour: '00h', before: 15, after: 12 },
    { hour: '02h', before: 12, after: 10 },
    { hour: '04h', before: 10, after: 8 },
    { hour: '06h', before: 25, after: 18 },
    { hour: '08h', before: 45, after: 30 },
    { hour: '10h', before: 55, after: 35 },
    { hour: '12h', before: 65, after: 40 },
    { hour: '14h', before: 60, after: 38 },
    { hour: '16h', before: 50, after: 32 },
    { hour: '18h', before: 70, after: 42 },
    { hour: '20h', before: 80, after: 48 },
    { hour: '22h', before: 40, after: 28 }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationProgress(prev => {
        if (prev >= 100) {
          setShowAfter(true);
          return 100;
        }
        return prev + 1;
      });
    }, 30);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (showAfter) {
      const timer = setTimeout(() => {
        setShowAfter(false);
        setAnimationProgress(0);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showAfter]);

  const maxValue = Math.max(...data.map(d => d.before));

  return (
    <div className="w-full max-w-4xl mx-auto" style={{ display: 'none' }}>
      <div className="mb-8 text-center">
        <h3 className="text-2xl font-bold text-neutral-800 mb-2">
          {t('home.consumption.title', 'Votre consommation journalière')}
        </h3>
        <p className="text-neutral-600">
          {showAfter
            ? t('home.consumption.after', 'Après avoir rejoint la communauté')
            : t('home.consumption.before', 'Avant la communauté d\'énergie')
          }
        </p>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-lg border border-neutral-200">
        <div className="flex items-end justify-between h-80 gap-4">
          {data.map((point, index) => {
            const height = showAfter
              ? (point.after / maxValue) * 100
              : (point.before / maxValue) * 100;

            const animatedHeight = (height * animationProgress) / 100;
            const savings = ((point.before - point.after) / point.before) * 100;

            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="relative w-full h-64 flex items-end">
                  <div
                    className="w-full rounded-t-lg transition-all duration-500 relative overflow-hidden"
                    style={{
                      height: `${animatedHeight}%`,
                      backgroundColor: showAfter ? '#10b981' : '#F8BD3D'
                    }}
                  >
                    {showAfter && (
                      <div className="absolute inset-0 bg-gradient-to-t from-emerald-600 to-emerald-400 opacity-50" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      {animationProgress > 80 && (
                        <span className="text-xs font-bold text-white">
                          {showAfter ? point.after : point.before}kW
                        </span>
                      )}
                    </div>
                  </div>
                  {showAfter && animationProgress > 90 && (
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap">
                      -{savings.toFixed(0)}%
                    </div>
                  )}
                </div>
                <span className="text-xs font-medium text-neutral-600">{point.hour}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F8BD3D' }} />
            <span className="text-sm text-neutral-600">
              {t('home.consumption.beforeLabel', 'Avant')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded bg-emerald-500" />
            <span className="text-sm text-neutral-600">
              {t('home.consumption.afterLabel', 'Après communauté')}
            </span>
          </div>
        </div>

        <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-neutral-700 text-center">
            {showAfter
              ? t('home.consumption.savingsMessage', 'Économisez jusqu\'à 40% sur vos coûts énergétiques')
              : t('home.consumption.standardMessage', 'Optimisez votre consommation avec notre communauté')
            }
          </p>
        </div>
      </div>
    </div>
  );
};
